import { devtools } from 'zustand/middleware'
import { shallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'

const AI_STORE_KEY = '@bitlyft/ai-panel'

type AIState = {
	isOpen: boolean
	activeSessionId: string | undefined

	/**
	 * Server-issued id for the in-progress conversation when no session was
	 * pre-selected. Captured from `X-Noah-Session-Id` on the first turn of a
	 * brand-new chat so the panel header can switch into "active session"
	 * mode (back arrow + title + rename) before the user has explicitly
	 * resumed a session. Kept distinct from `activeSessionId` because that
	 * field is the remount key for `<AIChatInner>` — writing to it mid-stream
	 * would discard live messages.
	 */
	currentSessionId: string | undefined

	/**
	 * Monotonic counter bumped on `newSession()` / `reset()`. Composed into the
	 * outer `<AIChat>` remount key so the back-arrow forces a fresh
	 * `<AIChatInner>` even when `activeSessionId` was already `undefined`
	 * (the fresh-chat case, where the server-issued id only ever lives in
	 * `currentSessionId`). Deliberately NOT bumped from `setCurrentSessionId`
	 * — see the comment on `currentSessionId` above for why that field must
	 * not drive remount.
	 */
	newSessionEpoch: number

	/**
	 * Active `useChat().stop` registered by `<AIChatInner>` on mount. The chat
	 * owns its own AbortController internally; `stop()` is the public way to
	 * cancel an in-flight stream. Other surfaces (tenant-switch effect, reset)
	 * call `abortActiveStream()` which delegates here.
	 */
	stopHandler: (() => void) | undefined
}

type AIActions = {
	openPanel: () => void
	closePanel: () => void
	togglePanel: () => void
	newSession: () => void
	selectSession: (id: string) => void
	setCurrentSessionId: (id: string | undefined) => void
	setStopHandler: (handler: (() => void) | undefined) => void
	abortActiveStream: () => void
	reset: () => void
}

type AIStore = AIState & {
	actions: AIActions
}

const initialState: AIState = {
	isOpen: false,
	activeSessionId: undefined,
	currentSessionId: undefined,
	newSessionEpoch: 0,
	stopHandler: undefined,
}

const aiStore = createWithEqualityFn<AIStore>()(
	devtools(
		(set, get) => ({
			...initialState,

			actions: {
				openPanel: () =>
					set(
						{
							isOpen: true,
							activeSessionId: undefined,
							currentSessionId: undefined,
						},
						false,
						'ai/openPanel',
					),

				closePanel: () => set({ isOpen: false }, false, 'ai/closePanel'),

				togglePanel: () => {
					const { isOpen } = get()
					if (isOpen) {
						set({ isOpen: false }, false, 'ai/togglePanel')
						return
					}
					set(
						{
							isOpen: true,
							activeSessionId: undefined,
							currentSessionId: undefined,
						},
						false,
						'ai/togglePanel',
					)
				},

				newSession: () =>
					set(
						(state) => ({
							activeSessionId: undefined,
							currentSessionId: undefined,
							newSessionEpoch: state.newSessionEpoch + 1,
						}),
						false,
						'ai/newSession',
					),

				selectSession: (id) =>
					set(
						{ activeSessionId: id, currentSessionId: undefined },
						false,
						'ai/selectSession',
					),

				setCurrentSessionId: (id) =>
					set({ currentSessionId: id }, false, 'ai/setCurrentSessionId'),

				setStopHandler: (handler) =>
					set({ stopHandler: handler }, false, 'ai/setStopHandler'),

				abortActiveStream: () => {
					const { stopHandler } = get()
					stopHandler?.()
				},

				reset: () => {
					// Stop any in-flight chat stream before clearing state.
					const { stopHandler, newSessionEpoch } = get()
					stopHandler?.()
					set(
						{ ...initialState, newSessionEpoch: newSessionEpoch + 1 },
						false,
						'ai/reset',
					)
				},
			},
		}),
		{ name: AI_STORE_KEY },
	),
	shallow,
)

// ─── React selectors ──────────────────────────────────────────────────────────

export const useIsOpen = () => aiStore((state) => state.isOpen)
export const useActiveSessionId = () =>
	aiStore((state) => state.activeSessionId)
/**
 * Display session id for the header — the active (selected) session takes
 * precedence; falls back to the server-issued id of an in-progress new chat.
 */
export const useDisplaySessionId = () =>
	aiStore((state) => state.activeSessionId ?? state.currentSessionId)
export const useNewSessionEpoch = () =>
	aiStore((state) => state.newSessionEpoch)
export const useAIActions = () => aiStore((state) => state.actions)

// ─── Vanilla getters (outside React) ──────────────────────────────────────────

export const getAIStore = () => aiStore.getState()

export { aiStore }
